import React, { useCallback, useEffect, useState } from 'react';
import { FeedsDataItem } from '../feeds_list_types';
import FeedInformationPanel from './FeedInformationPanel';
import ParsingTotalsPanel from './ParsingTotalsPanel';
import ErrorRecordsPanel from './ErrorRecordsPanel';
import AdditionalPanels from './AdditionalPanels';
import FeedDetailSkeleton from './FeedDetailSkeleton';
import FlyOutMenu from '../../../../core/components/FlyOutMenu';
import feedsRepo from '../../data/feedsRepo';
import { useAtomValue } from 'jotai';
import { feedsUserContextAtom } from '../feeds_list_atoms';

interface FeedDetailFlyOutProps {
    feedItem: FeedsDataItem;
    onCloseFlyoutAndRefreshTable?: () => void;
    open: boolean;
    onClose: () => void;
    setSelectedRows: (rows: any[], ignoreCurrentSelection?: boolean, replaceAll?: boolean) => void;
    ignoreClickRef?: React.RefObject<HTMLElement>;
}

const FeedDetailFlyOut = ({
    feedItem,
    onCloseFlyoutAndRefreshTable,
    open,
    onClose,
    setSelectedRows,
    ignoreClickRef,
}: FeedDetailFlyOutProps): JSX.Element => {
    const { userType } = useAtomValue(feedsUserContextAtom);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (feedItem?.Id && open) {
            setLoading(true);
            const timer = setTimeout(() => setLoading(false), 350);
            return (): void => clearTimeout(timer);
        }
    }, [feedItem?.Id, open]);

    const handleRefreshFeedData = useCallback(
        async (feedId: string): Promise<void> => {
            try {
                const detail = await feedsRepo.getDetail(feedId, userType === 'CE');
                const updatedRow = { ...feedItem, ...detail };

                // Use the setSelectedRows function properly - it expects an array and uses ignoreCurrentSelection
                // to force replace the existing selection with the updated data
                setSelectedRows([updatedRow], true);
            } catch (error) {
                console.error('Error refreshing feed detail:', error);
            }
        },
        [feedItem, setSelectedRows, userType],
    );

    return (
        <FlyOutMenu
            title="Feed Details"
            isOpen={open}
            onClose={onClose}
            width={800}
            overflowY="scroll"
            ignoreClickRef={ignoreClickRef}
        >
            <div className="feeds-detail-flyout">
                <div className="feed-detail-content">
                    {feedItem && !loading ? (
                        <>
                            <FeedInformationPanel
                                feedItem={feedItem}
                                onRefreshFeedData={handleRefreshFeedData}
                                onCloseFlyoutAndRefreshTable={onCloseFlyoutAndRefreshTable}
                            />
                            <ParsingTotalsPanel feedItem={feedItem} />
                            <ErrorRecordsPanel feedItem={feedItem} />
                            <AdditionalPanels feedItem={feedItem} />
                        </>
                    ) : feedItem && loading ? (
                        <FeedDetailSkeleton />
                    ) : null}
                </div>
            </div>
        </FlyOutMenu>
    );
};

export default FeedDetailFlyOut;
